import { Box, Flex, Spinner } from "@chakra-ui/react";
import Filters from "components/Rent/Filters";
import HomeTimeSearchBar from "components/Rent/HomeTimeSearchBar";
import ModelCheckbox from "components/Rent/ModelCheckbox";
import AddressTimeSearchBar from "components/Station/AddressTimeSearchBar";
import Map from "components/Station/Map";
import StationInfoDrawer from "components/Station/StationInfoDrawer";
import {
  addHours,
  addMinutes,
  addMonths,
  format,
  isAfter,
  isBefore,
  set,
} from "date-fns";
import { setDefaultDeliveryZone } from "lib/common";
import {
  addBufferHoursET,
  addBufferHoursST,
  getAllRangeTimes,
  getAvailableCarIdsForStation,
  getDatePickerSettingsByConfigs,
  getTimeStringsByAddMins,
} from "lib/order";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import { useRouter } from "next/router";
import { useEffect, useMemo, useState } from "react";
import { DateObject } from "react-multi-date-picker";
import appStore from "store";
import api from "store/api";

const Station = ({
  configs = [],
  deliveryZones,
  timeOptsUnion,
  latestOrderTimeUnion,
  cityUnion,
  cars,
  orders,
  top_models,
  instant_zone_ids,
}) => {
  const {
    selectedCity,
    selectedZone,
    isSearchAirport,
    selectedDays,
    modelsFilter,
  } = appStore;

  const [stations, setStations] = useState(
    deliveryZones.filter((zone) => zone.level_3 !== null)
  );
  const [filterOrders, setFilterOrders] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isAllRangeTimesLoaded, setIsAllRangeTimesLoaded] = useState(false);
  const [groupPrice, setGroupPrice] = useState([]);

  const [center, setCenter] = useState({
    lat: 25.0494511,
    lng: 121.5423058,
  });

  const OPEN_START = set(new Date(), {
    hours: 0,
    minutes: 0,
    seconds: 0,
  });

  const OPEN_END = set(addMonths(new Date(), 3), {
    hours: 23,
    minutes: 59,
    seconds: 59,
  });

  useEffect(() => {
    appStore.tempSelectedStartClock = "";
    appStore.tempSelectedEndClock = "";
  }, []);

  useEffect(() => {
    if (selectedDays.length !== 2) return;
    appStore.selectedStartClock = "09:00";
    appStore.selectedEndClock = "09:00";
    const st = new DateObject(
      selectedDays[0].format("YYYY/MM/DD") + " " + "09:00"
    );
    const et = new DateObject(
      selectedDays[1].format("YYYY/MM/DD") + " " + "09:00"
    );
    const data = {
      start_time: st.format("YYYY/MM/DD H:mm"),
      end_time: et.format("YYYY/MM/DD H:mm"),
    };
    api.getGroupPrice(data).then((resp) => {
      setGroupPrice(
        resp.data.data.map((item) => {
          const noTaxTotal = Math.ceil(item.float_discount_amount / 1.05);
          const diffTime = et.valueOf() / 1000 - st.valueOf() / 1000;
          const avgAmount = Math.ceil((noTaxTotal / (diffTime / 60 / 60)) * 24);
          return {
            ...item,
            avgAmount: avgAmount,
          };
        })
      );
    });
  }, [selectedDays]);

  useEffect(() => {
    if (!isSearchAirport && selectedCity == "" && selectedZone == "") {
      setDefaultDeliveryZone();
    }
    appStore.isSameAddress = true;
  }, [isSearchAirport, selectedCity, selectedZone]);

  useEffect(() => {
    api.getOrders().then((resp) => {
      appStore.orders = resp.data.data;
      setFilterOrders(
        resp.data.data.filter((order) => {
          return isBefore(new Date(order.start_time), OPEN_END);
        })
      );
    });
  }, []);

  const allRangeTimes = useMemo(() => {
    return getAllRangeTimes(OPEN_START, OPEN_END);
  }, []);

  useEffect(() => {
    if (cars.length === 0) return;
    const addUserSetting = async (list) => {
      const data = await Promise.all(
        list.map(async (car) => {
          car.owner_settings = configs.find(({ car_ids }) =>
            car_ids.includes(car.id)
          );
          return car;
        })
      );
      appStore.cars = data;
    };
    addUserSetting(cars);
    if (configs.length === 0) return;
    appStore.configs = configs;
    if (deliveryZones.length === 0) return;
    appStore.deliveryZones = deliveryZones;
    if (top_models.length === 0) return;
    appStore.top_models = top_models;
    if (Object.keys(cityUnion).length === 0) return;
    appStore.cityUnion = cityUnion;
  }, [cars, configs, deliveryZones, top_models, cityUnion]);

  useEffect(() => {
    if (configs.length === 0 || filterOrders.length === 0) return;

    const setAllRangeTimes = async () => {
      const now = new DateObject();
      if (selectedDays[0] > now.add(3, "months")) {
        appStore.selectedDays = [
          new DateObject().add(2, "days"),
          new DateObject().add(4, "days"),
        ];
      }

      filterOrders
        .map((o) => {
          return {
            ...o,
            start_time: o.start_time.replace(/-/g, "/"),
            end_time: o.end_time.replace(/-/g, "/"),
          };
        })
        .forEach((o) => {
          const { start_time, end_time, car_id } = o;

          const carBufferHours =
            configs.find((config) => {
              return config.car_ids.includes(car_id);
            })?.order_interval / 60;

          const thisOrderConfig = configs.find((config) =>
            config.car_ids.includes(car_id)
          );

          // if (thisOrderConfig === undefined) {
          //   console.log("o", o);
          // }

          let orderPickupZoneId = "";
          let orderReturnZoneId = "";
          let thisPickupSupportZone = {};
          let thisReturnSupportZone = {};

          if (
            o.pickup_zone_id !== null &&
            o.pickup_zone_id !== undefined &&
            o.return_zone_id !== null &&
            o.return_zone_id !== undefined
          ) {
            orderPickupZoneId = o.pickup_zone_id;
            orderReturnZoneId = o.return_zone_id;

            thisPickupSupportZone = thisOrderConfig.support_zones.find(
              (zone) => zone.id.toString() === orderPickupZoneId.toString()
            );

            thisReturnSupportZone = thisOrderConfig.support_zones.find(
              (zone) => zone.id.toString() === orderReturnZoneId.toString()
            );
          }

          let pickup_business_start_hour = "";
          let pickup_business_end_hour = "";

          if (
            thisPickupSupportZone === undefined ||
            Object.keys(thisPickupSupportZone).length === 0
          ) {
            pickup_business_start_hour = thisOrderConfig.business_start_hour;
            pickup_business_end_hour = thisOrderConfig.business_end_hour;
          } else {
            pickup_business_start_hour =
              thisPickupSupportZone.business_start_hour;
            pickup_business_end_hour = thisPickupSupportZone.business_end_hour;
          }

          let return_business_start_hour = "";
          let return_business_end_hour = "";

          if (
            thisReturnSupportZone === undefined ||
            Object.keys(thisReturnSupportZone).length === 0
          ) {
            return_business_start_hour = thisOrderConfig.business_start_hour;
            return_business_end_hour = thisOrderConfig.business_end_hour;
          } else {
            return_business_start_hour =
              thisReturnSupportZone.business_start_hour;
            return_business_end_hour = thisReturnSupportZone.business_end_hour;
          }

          const startTime = addBufferHoursST(
            addHours(new Date(start_time), 8), // 訂單時間轉換成台北時間
            carBufferHours,
            Number(pickup_business_start_hour.split(":")[0]),
            Number(
              pickup_business_end_hour.split(":")[0] === "00"
                ? "24"
                : pickup_business_end_hour.split(":")[0]
            )
          );

          const endTime = addBufferHoursET(
            addHours(new Date(end_time), 8),
            carBufferHours,
            Number(return_business_start_hour.split(":")[0]),
            Number(
              return_business_end_hour.split(":")[0] === "00"
                ? "24"
                : return_business_end_hour.split(":")[0]
            )
          );

          let curTime = startTime;
          while (curTime <= endTime) {
            const date = format(curTime, "yyyy/MM/dd");
            const time = format(curTime, "HH:mm");
            if (allRangeTimes[date]) {
              if (!allRangeTimes[date][time]?.cars.includes(car_id)) {
                allRangeTimes[date][time]?.cars.push(car_id);
              }
              allRangeTimes[date][time]?.orders.push(o);
            }
            curTime = addMinutes(curTime, 30);
          }

          function generateTimes(baseTime, mins) {
            return mins.map((min) =>
              getTimeStringsByAddMins(new Date(baseTime), min)
            );
          }

          function updateAllRangeTimes(time) {
            const mainKey = time[0];
            const subKey = time[1];

            if (allRangeTimes[mainKey] && allRangeTimes[mainKey][subKey]) {
              if (
                allRangeTimes[mainKey][subKey].guys[thisOrderConfig.location_id]
              ) {
                allRangeTimes[mainKey][subKey].guys[
                  thisOrderConfig.location_id
                ] += 1;
              } else {
                allRangeTimes[mainKey][subKey].guys[
                  thisOrderConfig.location_id
                ] = 1;
              }
            }
          }

          const timesMins = [7, 7.5, 8, 8.5, 9];
          const startTimes = generateTimes(start_time, timesMins);
          const endTimes = generateTimes(end_time, timesMins);

          startTimes.forEach(updateAllRangeTimes);
          endTimes.forEach(updateAllRangeTimes);
        });

      setIsAllRangeTimesLoaded(true);
      appStore.allRangeTimes = allRangeTimes;
    };

    setAllRangeTimes();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configs, filterOrders]);

  // console.log("allRangeTimes", allRangeTimes);
  // useEffect(() => {
  //   if (!isAllRangeTimesLoaded) return;
  //   setIsLoading(true);
  //   const initStations = async () => {
  //     const _stations = await Promise.all(
  //       deliveryZones
  //         .filter((zone) => zone.level_3 !== null)
  //         .map((zone) => {
  //           // 從 configs 中找出有支援該 zone 的 config
  //           const supportConfigs = configs
  //             .filter((config) =>
  //               config.support_zones.some(
  //                 (supportZone) =>
  //                   supportZone.id.toString() === zone.id.toString()
  //               )
  //             )
  //             .map((config) => {
  //               const car = cars.filter((_car) => {
  //                 return config.car_ids.includes(_car.id);
  //               });
  //               return {
  //                 ...config,
  //                 car,
  //               };
  //             });

  //           let openSt = "";
  //           let openEd = "";
  //           supportConfigs.forEach((config) => {
  //             const thisZone = config.support_zones.find(
  //               (_zone) => _zone.id.toString() === zone.id.toString()
  //             );
  //             if (thisZone) {
  //               if (thisZone.business_start_hour < openSt || openSt === "") {
  //                 openSt = thisZone.business_start_hour;
  //               }
  //               if (thisZone.business_end_hour > openEd || openEd === "") {
  //                 openEd = thisZone.business_end_hour;
  //               }
  //             }
  //           });

  //           let carIds = [];

  //           supportConfigs.forEach((config) => {
  //             carIds = [...carIds, ...config.car_ids];
  //           });

  //           const startDate = selectedDays[0].format("YYYY/MM/DD");
  //           const endDate = selectedDays[1].format("YYYY/MM/DD");

  //           const availableCarIds = getAvailableCarIdsForStation(
  //             startDate,
  //             endDate,
  //             allRangeTimes,
  //             carIds,
  //             configs,
  //             zone.id
  //           );

  //           // console.log("availableCarIds", availableCarIds);

  //           let lowestModel = {};
  //           if (availableCarIds.length === 0) {
  //             if (supportConfigs.length !== 0) {
  //               const allCars = supportConfigs
  //                 .map((config) => config.car)
  //                 .flat();
  //               lowestModel = allCars.reduce((lowest, current) => {
  //                 return lowest.price < current.price ? lowest : current;
  //               });
  //             }
  //           } else {
  //             const allCars = availableCarIds.map((carId) => {
  //               return supportConfigs
  //                 .map((config) => config.car)
  //                 .flat()
  //                 .find((car) => car.id === carId);
  //             });

  //             lowestModel = allCars.reduce((lowest, current) => {
  //               return lowest.price < current.price ? lowest : current;
  //             });
  //           }

  //           const availableCarList = cars.filter(
  //             (car) =>
  //               availableCarIds.includes(car.id) &&
  //               modelsFilter[car.model_name.replace(" ", "")]
  //           );

  //           const allSupportThisZoneCarList = cars.filter((car) => {
  //             return carIds.includes(car.id);
  //           });
  //           const noCarList = allSupportThisZoneCarList.filter((car) => {
  //             return (
  //               !availableCarIds.includes(car.id) &&
  //               modelsFilter[car.model_name.replace(" ", "")]
  //             );
  //           });

  //           return {
  //             ...zone,
  //             supportConfigs,
  //             lowestModel: lowestModel || {},
  //             openSt,
  //             openEd,
  //             hasAvailableCar: availableCarList.length > 0,
  //             availableCarList,
  //             noCarList,
  //             allSupportThisZoneCarList,
  //           };
  //         })
  //     );

  //     setStations(_stations);
  //     setIsLoading(false);
  //   };

  //   initStations();
  // }, [
  //   allRangeTimes,
  //   cars,
  //   configs,
  //   deliveryZones,
  //   isAllRangeTimesLoaded,
  //   selectedDays,
  //   modelsFilter,
  // ]);

  // console.log("stations", stations);
  // console.log("cars", cars);

  if (stations.length === 0) {
    return (
      <Flex justify="center" align="center" h="100vh">
        <Spinner />
      </Flex>
    );
  }
  return (
    <Box
      w="100vw"
      maxH={{ base: "calc(100svh - 50px)", md: "calc(100vh - 72px)" }}
      h={{ base: "calc(100svh - 50px)", md: "calc(100vh - 72px)" }}
      overflow="hidden"
    >
      {/* header */}
      <Box flex="1" w="100%" zIndex="998" pos="relative">
        <Flex
          justify="center"
          w="100%"
          bg="white"
          border="1px solid rgba(0, 0, 0, 0)"
          pos="relative"
          zIndex="1"
        >
          <Box
            w="100%"
            maxW={{ base: "90%", md: "960px" }}
            alignItems="center"
            justifyContent="center"
            zIndex="1"
            pos="relative"
          >
            <AddressTimeSearchBar cityUnion={cityUnion} setCenter={setCenter} />
          </Box>
        </Flex>
        <Flex
          justify="center"
          bg="white"
          pb="16px"
          transition="all .3s ease"
          transform="translateY(0)"
          borderBottom="1px solid rgba(0, 0, 0, .1)"
        >
          <Flex
            maxW={{ base: "90%", md: "960px" }}
            alignItems="center"
            justifyContent={{ base: "space-between", md: "center" }}
            pos="relative"
            pb={{ base: "0px", md: "0px" }}
            overflow={{ base: "hidden", md: "visible" }}
          >
            <ModelCheckbox />
          </Flex>
        </Flex>
      </Box>

      <Flex
        zIndex="9"
        pos="relative"
        flex="1"
        w="100%"
        h={{ base: "calc(100svh - 199px)", lg: "calc(100vh - 221px)" }}
      >
        {/* {isLoading && (
          <Flex
            pos="absolute"
            top="0"
            left="0"
            zIndex="9990"
            w="100%"
            h={{ base: "calc(100vh - 199px)", lg: "calc(100vh - 221px)" }}
            bg="rgba(0, 0, 0, .2)"
            justify="center"
            align="center"
          >
            <Spinner />
          </Flex>
        )} */}
        <Box zIndex="99" pos="relative">
          <StationInfoDrawer
            // stations={stations}
            deliveryZones={deliveryZones}
            groupPrice={groupPrice}
            top_models={top_models}
            isAllRangeTimesLoaded={isAllRangeTimesLoaded}
            configs={configs}
            cars={cars}
            allRangeTimes={allRangeTimes}
            instant_zone_ids={instant_zone_ids}
          />
        </Box>

        <Map
          stations={stations}
          configs={configs}
          center={center}
          setCenter={setCenter}
          groupPrice={groupPrice}
          instant_zone_ids={instant_zone_ids}
        />
      </Flex>
    </Box>
  );
};

export async function getServerSideProps(context) {
  const { locale } = context;
  const settingResp = await api.getAllOwnerSetting();
  const deliveryZoneResp = await api.getDeliveryZone();
  const carsResp = await api.getCars();
  const cars = carsResp.data.data;
  const top_models = carsResp.data.top_models;
  const instant_zone_ids = carsResp.data.instant_zone_ids;
  const configs = settingResp.data.data.map((config) => ({
    ...config,
    owner_id: config.location_id || "udrive",
  }));

  const deliveryZones = deliveryZoneResp.data.data;
  const { latestOrderTime, timeOpts, deliveryZonesObj } =
    getDatePickerSettingsByConfigs(
      settingResp.data.data,
      deliveryZoneResp.data.data
    );
  const timeOptsUnion = timeOpts;
  const latestOrderTimeUnion = latestOrderTime;
  const cityUnion = deliveryZonesObj;
  return {
    props: {
      ...(await serverSideTranslations(locale, ["common"])),
      configs: configs || [],
      deliveryZones: deliveryZones || [],
      timeOptsUnion: timeOptsUnion || [],
      latestOrderTimeUnion: latestOrderTimeUnion || 0,
      cityUnion: cityUnion || {},
      cars: cars || [],
      top_models: top_models || [],
      instant_zone_ids: instant_zone_ids || [],
    },
  };
}

export default Station;
